import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Table } from "reactstrap";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { findTableColumnDefinitionByPreferenceKey } from "helpers/GeneralHelper";
import { useTableColumnPreferences } from "libs/context/TableColumnPreferencesContext";
import ReportTableToolbar from "components/Common/ReportTableToolbar";

const getReviewStatusBadgeClass = (statusValue = "") => {
    const normalized = String(statusValue).trim().toLowerCase();
    if (normalized === "red") return "team-performance-review-badge--red";
    if (normalized === "yellow") return "team-performance-review-badge--yellow";
    if (normalized === "green") return "team-performance-review-badge--green";
    return "team-performance-review-badge--neutral";
};

const formatReviewStatusLabel = (value = "") => {
    const normalized = String(value).trim();
    if (!normalized) return "";
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
};

const renderReviewStatusCell = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return <span className="text-muted">—</span>;
    }

    const badgeClass = getReviewStatusBadgeClass(value);
    const label = formatReviewStatusLabel(value);

    return (
        <span
            className={`px-2 py-1 fw-semibold team-performance-review-badge ${badgeClass}`.trim()}
        >
            {label}
        </span>
    );
};

const parseReviewStatusLabel = (reviewStatusHtml = "") => {
    const raw = String(reviewStatusHtml ?? "").trim();
    if (!raw) return "";
    return raw.replace(/<[^>]*>/g, "").trim();
};

const resolveExportCellValue = (column, row = {}) => {
    const accessor = column?.accessor;
    const value = row?.[accessor];

    if (column?.isReviewStatusHtml) {
        if (row?.reviewStatusHtml) {
            return parseReviewStatusLabel(row.reviewStatusHtml);
        }
        return formatReviewStatusLabel(row?.reviewStatus ?? value) || "";
    }

    if (column?.isStatusValue) {
        if (value === null || value === undefined || value === "") {
            return "";
        }
        return String(value);
    }

    if (value === null || value === undefined) {
        return "";
    }

    return String(value);
};

const renderBodyCell = (row, column) => {
    const accessor = column?.accessor;
    const value = row?.[accessor];

    if (column?.isReviewStatusHtml) {
        if (row?.reviewStatusHtml) {
            return (
                <span
                    className="team-performance-review-status-html"
                    dangerouslySetInnerHTML={{ __html: row.reviewStatusHtml }}
                />
            );
        }
        return renderReviewStatusCell(row?.reviewStatus ?? value);
    }

    if (column?.isStatusValue) {
        if (value === null || value === undefined || value === "") {
            return <span className="text-muted">—</span>;
        }
        if (column?.isPlainMetric) {
            return <span className="team-performance-calls-value">{value}</span>;
        }
        return <span className="status-columns-report-value">{value}</span>;
    }

    if (value === null || value === undefined || value === "") {
        return <span className="text-muted">—</span>;
    }

    return <span>{value}</span>;
};

const flattenTeamPerformanceRows = (teams = []) => {
    const flattened = [];
    (Array.isArray(teams) ? teams : []).forEach((teamObj = {}) => {
        (Array.isArray(teamObj?.members) ? teamObj.members : []).forEach((memberObj = {}) => {
            (Array.isArray(memberObj?.rows) ? memberObj.rows : []).forEach((row = {}) => {
                flattened.push(row);
            });
        });
    });
    return flattened;
};

const TeamPerformanceCustomTable = ({
    columns = [],
    data = [],
    isActionRequired = false,
    pageName = "TeamPerformanceReport",
    showCopy = false,
    showCsv = false,
}) => {
    const resolvedTableKey = String(pageName || "TeamPerformanceReport").trim();
    const {
        columnOrder,
        visibleColumns,
        visibleColumnOrder,
        orderedColumns,
        handleColumnDragEnd,
        handleColumnToggle,
    } = useTableColumnPreferences(resolvedTableKey, columns);

    const visibleColumnDefinitions = useMemo(
        () =>
            visibleColumnOrder
                .map((preferenceKey) =>
                    findTableColumnDefinitionByPreferenceKey(columns, preferenceKey)
                )
                .filter(Boolean),
        [columns, visibleColumnOrder]
    );

    const sortedData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const flattenedRows = useMemo(
        () => flattenTeamPerformanceRows(sortedData),
        [sortedData]
    );

    const getExportRows = useCallback(
        (exportColumns = []) =>
            flattenedRows.map((row) =>
                exportColumns.map((column) => resolveExportCellValue(column, row))
            ),
        [flattenedRows]
    );

    const headerColSpan = visibleColumnDefinitions.length + (isActionRequired ? 1 : 0);

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        cursor: "grab",
        background: isDragging ? "#f1f5f9" : "transparent",
        ...draggableStyle,
    });

    const hasRows = sortedData.some((team) =>
        (Array.isArray(team?.members) ? team.members : []).some(
            (member) => Array.isArray(member?.rows) && member.rows.length > 0
        )
    );

    return (
        <Card className="shadow-sm border-0 team-performance-table-wrap">
            <CardBody className="p-0">
                <ReportTableToolbar
                    pageName={pageName}
                    tableKey={resolvedTableKey}
                    columns={columns}
                    visibleColumnOrder={visibleColumnOrder}
                    columnOrder={columnOrder}
                    orderedColumns={orderedColumns}
                    visibleColumns={visibleColumns}
                    onColumnToggle={handleColumnToggle}
                    getExportRows={getExportRows}
                    showCopy={showCopy}
                    showCsv={showCsv}
                />

                <div className="table-responsive">
                    <Table
                        hover
                        borderless
                        className="align-middle mb-0 custom-table team-performance-table"
                    >
                        <DragDropContext onDragEnd={handleColumnDragEnd}>
                            <Droppable
                                droppableId="team-performance-columns"
                                direction="horizontal"
                            >
                                {(provided) => (
                                    <thead
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        style={{ backgroundColor: "#f8fafc" }}
                                    >
                                        <tr
                                            style={{
                                                color: "#374151",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                            }}
                                        >
                                            {visibleColumnDefinitions.map((col, index) => (
                                                <Draggable
                                                    key={col.accessor}
                                                    draggableId={col.accessor}
                                                    index={index}
                                                >
                                                    {(dragProvided, snapshot) => (
                                                        <th
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            {...dragProvided.dragHandleProps}
                                                            className="text-nowrap"
                                                            style={getItemStyle(
                                                                snapshot.isDragging,
                                                                dragProvided.draggableProps.style
                                                            )}
                                                        >
                                                            {col?.Header}
                                                        </th>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            {isActionRequired ? (
                                                <th className="text-center" />
                                            ) : null}
                                        </tr>
                                    </thead>
                                )}
                            </Droppable>

                            <tbody style={{ backgroundColor: "#ffffff" }}>
                                {!hasRows ? (
                                    <tr>
                                        <td
                                            colSpan={headerColSpan}
                                            className="text-center text-muted py-5"
                                        >
                                            No data found
                                        </td>
                                    </tr>
                                ) : (
                                    sortedData.map((teamObj, teamIndex) => (
                                        <React.Fragment key={`team-${teamIndex}`}>
                                            <tr className="team-performance-team-row">
                                                <td colSpan={headerColSpan} className="fw-bold py-2">
                                                    <span className="me-3">
                                                        Team : {teamObj?.team || "—"}
                                                    </span>
                                                    <span>
                                                        Team Lead : {teamObj?.teamLead || "—"}
                                                    </span>
                                                </td>
                                            </tr>

                                            {(Array.isArray(teamObj?.members)
                                                ? teamObj.members
                                                : []
                                            ).map((memberObj, memberIndex) => (
                                                <React.Fragment
                                                    key={`member-${teamIndex}-${memberIndex}`}
                                                >
                                                    <tr className="team-performance-member-row">
                                                        <td
                                                            colSpan={headerColSpan}
                                                            className="fw-semibold py-2"
                                                        >
                                                            Member : {memberObj?.name || "—"}
                                                        </td>
                                                    </tr>

                                                    {(Array.isArray(memberObj?.rows)
                                                        ? memberObj.rows
                                                        : []
                                                    ).map((row, rowIndex) => (
                                                        <tr
                                                            key={`row-${teamIndex}-${memberIndex}-${rowIndex}`}
                                                        >
                                                            {visibleColumnDefinitions.map((col) => (
                                                                <td key={`${col.accessor}-${rowIndex}`}>
                                                                    {renderBodyCell(row, col)}
                                                                </td>
                                                            ))}
                                                            {isActionRequired ? <td /> : null}
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </DragDropContext>
                    </Table>
                </div>
            </CardBody>
        </Card>
    );
};

TeamPerformanceCustomTable.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    isActionRequired: PropTypes.bool,
    pageName: PropTypes.string,
    showCopy: PropTypes.bool,
    showCsv: PropTypes.bool,
};

export default TeamPerformanceCustomTable;
